// eslint-disable-next-line no-unused-vars
import React from 'react';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import {
  Button,
  ComposedModal,
  DataTable,
  DataTableSkeleton,
  InlineNotification,
  Loading,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TextArea,
} from '@carbon/react';
import {
  ArrowDownUp,
  ArrowDownWideNarrow,
  ArrowUpNarrowWide,
  MessageSquare,
} from 'lucide-react';
import { array, bool, func, number, object, shape, string } from 'prop-types';

import { NWDropdown, NWTextInput } from 'Components/shared';
import ServicePagination from 'Containers/Service/ServicePagination';

import IconPDF from 'Assets/images/pdf.svg';

const StyledPPWR = styled.div`
  min-width: 0;

  .filterSection {
    display: flex;
    flex-direction: column;
    padding: 25px 0;
    gap: 20px;

    .filterContainer {
      display: flex;
      flex-direction: column;
      gap: 20px;
    }

    .filters {
      display: grid;
      grid-template-columns: repeat(4, minmax(0, 1fr));
      align-items: flex-start;
      gap: 10px;
    }

    .cds--form-item {
      min-width: 0;
    }

    .cds--label {
      display: none;
    }

    .cds--select-input,
    .cds--text-input {
      background: #fff;
      border: 1px solid #e2e2e2;
      border-radius: 5px;
    }

    .styledDropdown {
      margin: 10px 0 15px;
    }

    .actionsSection {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      gap: 10px;

      button.cds--btn {
        height: 40px;
        max-width: none;
        min-width: 170px;
        justify-content: center;
        font-size: 1rem;
        text-align: center;

        &.deleteFilters {
          border: 1px solid #D52B1E;
          color: #D52B1E;

          &:hover {
            background: transparent;
          }
        }
      }
    }
  }

  .cds--inline-notification {
    color: #3a3a3a;
    max-width: 100%;
    margin-bottom: 10px;
    border: none;
    background: #f5f7f9;

    &.cds--inline-notification--error {
      background: #fff2f1;
    }

    p {
      font-size: 14px;
      line-height: 20px;
    }

    .cds--inline-notification__close-button .cds--inline-notification__close-icon {
      fill: #3a3a3a;
    }
  }

  .cds--data-table {
    border: 1px solid #e2e2e2;

    th {
      background-color: #f5f7f9;

      .cds--table-header-label {
        button {
          display: flex;
          align-items: center;
          width: 100%;
          padding: 0;
          border: none;
          background: transparent;
          cursor: pointer;
          font: inherit;
          gap: 5px;

          svg {
            width: 18px;
            min-width: 18px;
            height: 18px;
            stroke: #707070;
          }
        }
      }

      &:nth-of-type(1) {
        width: 15%;
      }

      &:nth-of-type(2) {
        width: 28%;
      }

      &:nth-of-type(3) {
        width: 13%;
      }

      &:nth-of-type(4) {
        width: 20%;
      }

      &:nth-of-type(5) {
        width: 12%;
      }

      &:nth-of-type(6) {
        width: 12%;
        text-align: center;
      }
    }

    td {
      background: #fff;

      &:nth-of-type(6) {
        text-align: center;
      }
    }

    tbody tr:hover td {
      background: #f5f7f9;
    }
  }

  .documentStatus {
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    border-radius: 2px;
    font-size: 13px;
    line-height: 16px;
    white-space: nowrap;

    &.technical_documentation {
      color: #198754;
      background: #e8f5ee;
    }

    &.self_declaration {
      color: #1f59a2;
      background: #e7ecf2;
    }

    &.pending_technical_documentation {
      color: #9a6b16;
      background: #fff4dc;
    }

    &.requested {
      color: #1f59a2;
      background: #e7ecf2;
    }

    &.unknown {
      color: #707070;
      background: #f3f2f2;
    }
  }

  .documentActions {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    gap: 5px;

    .requestDocument {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 7px;
      min-height: 32px;
      padding: 5px 12px;
      border: 1px solid #dde6f2;
      border-radius: 2px;
      background: #fff;
      color: #3a3a3a;
      cursor: pointer;
      font-size: 14px;

      svg {
        width: 17px;
        height: 17px;
      }

      &:hover {
        border-color: #1f59a2;
        background: #eef3f9;
        color: #1f59a2;
      }
    }

    button.fileType {
      padding: 7px 3px;
      border: none;
      background: transparent;
      cursor: pointer;
      transform: none;
      min-width: 30px;
      min-height: 31px;

      svg {
        width: 24px;
        height: 30px;
      }

      &:disabled {
        cursor: not-allowed;
        opacity: 0.5;
      }
    }
  }

  .cds--skeleton__placeholder {
    width: 100%;
    height: 42px;
    margin-top: 14px;
  }

  @media screen and (max-width: ${ props => props.theme.device.laptop }) {
    .filterSection {
      .filters {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }

      .cds--label {
        display: inline-block;
        color: #3a3a3a;
        font-size: 15px;
        font-weight: 600;
      }
    }

    .cds--skeleton.cds--data-table-container {
      overflow: hidden;
      max-width: calc(100vw - 34px);
    }

    .cds--data-table {
      border: none;

      > thead {
        display: none;
      }

      > tbody {
        background: #fff;

        tr {
          display: flex;
          flex-direction: column;
          height: auto;
          margin-top: 20px;
          border: 1px solid #e2e2e2;

          td {
            border: none;

            &:first-of-type {
              height: 40px;
              background: #f5f7f9;
            }

            &:nth-of-type(6) {
              text-align: left;

              .documentActions {
                justify-content: flex-start;
              }
            }
          }
        }
      }
    }
  }

  @media screen and (max-width: 600px) {
    .filterSection {
      .filters {
        grid-template-columns: 1fr;
        gap: 10px;

        > div,
        .styledDropdown {
          margin: 0;
        }

        .cds--text-input-wrapper {
          margin-bottom: 0;
        }
      }

      .actionsSection {
        flex-direction: column;

        button.cds--btn {
          width: 100%;
          min-width: 0;
        }
      }
    }
  }
`;

const StyledTableCell = styled(TableCell)`
  .productTitle {
    display: -webkit-box;
    max-width: 100%;
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
  }

  @media screen and (max-width: ${ props => props.theme.device.laptop }) {
    position: relative;
    display: flex;
    align-items: center;
    box-sizing: border-box;
    min-height: 40px;
    padding-left: 175px !important;

    &:before {
      content: "${ props => props.name }:";
      position: absolute;
      left: 10px;
      width: 165px;
      overflow: hidden;
      font-weight: 600;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
`;

const StyledRequestModal = styled(ComposedModal)`
  .cds--modal-container {
    width: calc(100% - 40px);
    max-width: 850px;
    max-height: calc(100vh - 80px);
  }

  .cds--modal-content {
    margin: 0;
    padding: 28px 32px;
  }

  .requestDescription {
    margin-bottom: 20px;
    color: #5f5f60;
    font-size: 18px;
    line-height: 1.5;
  }

  .requestDetails {
    margin-bottom: 24px;
  }

  .requestDetail {
    display: grid;
    grid-template-columns: 290px minmax(0, 1fr);
    gap: 20px;
    padding: 12px 0;
    border-bottom: 1px solid #e2e2e2;
    font-size: 18px;

    .label {
      color: #707070;
    }

    .value {
      color: #3a3a3a;
      overflow-wrap: anywhere;
    }
  }

  .cds--text-area {
    min-height: 110px;
    background: #fff;
  }

  .cds--modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 20px;
    height: auto;
    padding: 0 32px 28px;
    background: #fff;

    button.cds--btn {
      display: flex;
      align-items: center;
      justify-content: center;
      flex: 0 0 220px !important;
      width: 220px;
      max-width: 220px !important;
      min-height: 48px;
      padding: 0 24px !important;
      text-align: center;
    }

    .cancelRequest {
      border: 1px solid #1f59a2 !important;
      background: #fff;
      color: #1f59a2;

      &:hover {
        border-color: #1f59a2;
        background: #eef3f9;
        color: #1f59a2;
      }
    }
  }

  @media screen and (max-width: 600px) {
    .cds--modal-container {
      width: calc(100% - 20px);
    }

    .cds--modal-content {
      padding: 24px 20px;
    }

    .requestDescription,
    .requestDetail {
      font-size: 16px;
    }

    .requestDetail {
      grid-template-columns: 110px minmax(0, 1fr);
      gap: 12px;
    }

    .cds--modal-footer {
      gap: 10px;
      padding: 0 20px 20px;

      button.cds--btn {
        flex: 1 1 0 !important;
        width: auto;
        max-width: none !important;
        min-width: 0;
        min-height: 48px;
      }
    }
  }
`;

const PPWRComponent = ({
  products,
  pagination,
  filters,
  loading,
  notice,
  requester,
  selectedProduct,
  requestPending,
  requestErrors,
  requestedIds,
  downloadingId,
  currentSortId,
  filterErrors,
  registerFilter,
  registerRequest,
  validators,
  onFilterChange,
  onApplyFilters,
  onClearFilters,
  onSort,
  onDismissNotice,
  onOpenRequest,
  onCloseRequest,
  onSubmitRequest,
  onDownload,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const hasActiveFilters = Object.entries(filters)
    .some(([key, value]) => !['page', 'sort', 'order'].includes(key) && value);
  const documentTypes = [
    { id: 'all', value: '', label: t('ppwr.allDocuments') },
    { id: 'technical', value: '1', label: t('ppwr.technical_documentation') },
    { id: 'withoutDatasheet', value: '0', label: t('ppwr.withoutDatasheet') },
  ];
  const selectedDocumentType = documentTypes.find(
    item => item.value === (filters.has_ppwr_document || '')
  );
  const headers = [
    { key: 'code', header: t('ppwr.code') },
    { key: 'title', header: t('ppwr.title') },
    { key: 'manufacturerName', header: t('ppwr.brand') },
    { key: 'ppwrDocumentStatus', header: t('ppwr.documentType') },
    { key: 'ppwrDocumentUpdatedAt', header: t('ppwr.updated') },
    { key: 'actions', header: t('ppwr.action') },
  ];
  const requesterName = [requester?.firstName, requester?.lastName]
    .filter(Boolean).join(' ');
  const requesterDetails = [requesterName, requester?.email]
    .filter(Boolean).join(' — ') || '—';

  const formatDate = value => value
    ? value.split(' ')[0].split('-').reverse().join('.')
    : '—';

  const renderActions = product => {
    const requested = product.ppwrDocumentRequestedAt
      || requestedIds.includes(product.productId);

    return (
      <div className="documentActions">
        { ['technical_documentation', 'self_declaration'].includes(product.ppwrDocumentStatus) && (
          <button
            type="button"
            className="download fileType"
            aria-label={ t('ppwr.download') }
            title={ t('ppwr.download') }
            disabled={ downloadingId !== null }
            onClick={ () => onDownload(product) }
          >
            <IconPDF />
          </button>
        ) }
        { product.ppwrDocumentStatus === 'pending_technical_documentation'
          && !requested && (
          <button
            type="button"
            className="requestDocument"
            onClick={ () => onOpenRequest(product) }
          >
            <MessageSquare />
            { t('ppwr.request') }
          </button>
        ) }
      </div>
    );
  };

  const renderCell = (cell, row) => {
    const product = products.find(item => item.id === row.id);

    if (cell.info.header === 'title') {
      return (
        <span className="productTitle">
          { cell.value }
        </span>
      );
    }

    if (cell.info.header === 'ppwrDocumentStatus') {
      const requested = product.ppwrDocumentRequestedAt
        || requestedIds.includes(product.productId);

      if (
        cell.value === 'pending_technical_documentation'
        && requested
      ) {
        return (
          <span className="documentStatus requested">
            { t('ppwr.requested') }
          </span>
        );
      }

      const supportedStatuses = [
        'technical_documentation',
        'self_declaration',
        'pending_technical_documentation',
      ];

      const statusClass = supportedStatuses.includes(cell.value)
        ? cell.value
        : 'unknown';
      const statusLabel = supportedStatuses.includes(cell.value)
        ? `ppwr.${ cell.value }`
        : 'ppwr.unknownStatus';

      return (
        <span className={ `documentStatus ${ statusClass }` }>
          { t(statusLabel) }
        </span>
      );
    }

    if (cell.info.header === 'ppwrDocumentUpdatedAt') {
      return formatDate(cell.value);
    }

    if (cell.info.header === 'actions') {
      return renderActions(product);
    }

    return cell.value;
  };

  const renderTableHeader = header => {
    const sortFields = {
      code: 'code',
      title: 'title',
      manufacturerName: 'manufacturer_name',
      ppwrDocumentStatus: 'document_created_at',
      ppwrDocumentUpdatedAt: 'document_updated_at',
    };
    const sortField = sortFields[header.key];

    if (!sortField) {
      return <span>{ header.header }</span>;
    }

    const [currentSort, currentOrder] = currentSortId.split('-');
    const isSorted = currentSort === sortField;
    let sortIcon = <ArrowDownUp />;
    let sortDirection = 'asc';

    if (isSorted) {
      sortIcon = currentOrder === 'asc'
        ? <ArrowUpNarrowWide />
        : <ArrowDownWideNarrow />;
      sortDirection = currentOrder === 'asc' ? 'desc' : 'none';
    }

    return (
      <button
        type="button"
        onClick={ () => onSort(`${ sortField }-${ sortDirection }`) }
      >
        <span>{ header.header }</span>
        { sortIcon }
      </button>
    );
  };

  return (
    <StyledPPWR theme={ theme }>
      <Helmet>
        <title>{ t('ppwr.heading') }</title>
      </Helmet>
      <InlineNotification
        kind="info"
        title={ t('common.important') }
        subtitle={ t('ppwr.intro') }
      />
      <form className="filterSection" onSubmit={ onApplyFilters }>
        <div className="filterContainer">
          <div className="filters">
            <NWTextInput
              id="ppwr-code"
              labelText={ t('ppwr.code') }
              placeholder={ t('ppwr.code') }
              size="md"
              maxLength={ 40 }
              invalid={ !!filterErrors.code?.message }
              invalidText={ filterErrors.code?.message }
              { ...registerFilter('code', validators.code) }
            />
            <NWTextInput
              id="ppwr-title"
              labelText={ t('ppwr.title') }
              placeholder={ t('ppwr.title') }
              size="md"
              maxLength={ 300 }
              invalid={ !!filterErrors.title?.message }
              invalidText={ filterErrors.title?.message }
              { ...registerFilter('title', validators.title) }
            />
            <NWTextInput
              id="ppwr-manufacturer_name"
              labelText={ t('ppwr.brand') }
              placeholder={ t('ppwr.brand') }
              size="md"
              maxLength={ 255 }
              invalid={ !!filterErrors.manufacturer_name?.message }
              invalidText={ filterErrors.manufacturer_name?.message }
              { ...registerFilter('manufacturer_name', validators.manufacturer_name) }
            />
            <NWDropdown
              className="documentType"
              id="ppwr-type"
              titleText={ t('ppwr.documentType') }
              label={ t('ppwr.allDocuments') }
              size="md"
              items={ documentTypes }
              selectedItem={ selectedDocumentType }
              itemToString={ item => item?.label || '' }
              onChange={ ({ selectedItem }) => onFilterChange(
                'has_ppwr_document',
                selectedItem?.value || ''
              ) }
            />
          </div>
          <div className="actionsSection">
            <Button type="submit" kind="danger" className="filterOrders">
              { t('orders.filter') }
            </Button>
            { hasActiveFilters && (
              <Button
                type="button"
                kind="danger--ghost"
                className="deleteFilters"
                onClick={ onClearFilters }
              >
                { t('ppwr.reset') }
              </Button>
            ) }
          </div>
        </div>
      </form>
      { notice && (
        <InlineNotification
          kind="success"
          title={ notice }
          onCloseButtonClick={ onDismissNotice }
        />
      ) }
      <StyledRequestModal
        open={ !!selectedProduct }
        onClose={ onCloseRequest }
        selectorPrimaryFocus="#ppwr-request-notes"
      >
        <ModalHeader>
          <h4>{ t('ppwr.requestDatasheet') }</h4>
        </ModalHeader>
        <ModalBody>
          <p className="requestDescription">
            { t('ppwr.requestDescription') }
          </p>
          <div className="requestDetails">
            <div className="requestDetail">
              <span className="label">{ t('ppwr.code') }</span>
              <span className="value">{ selectedProduct?.code || '—' }</span>
            </div>
            <div className="requestDetail">
              <span className="label">{ t('ppwr.title') }</span>
              <span className="value">{ selectedProduct?.title || '—' }</span>
            </div>
            <div className="requestDetail">
              <span className="label">{ t('ppwr.brand') }</span>
              <span className="value">
                { selectedProduct?.manufacturerName || '—' }
              </span>
            </div>
            <div className="requestDetail">
              <span className="label">{ t('ppwr.requester') }</span>
              <span className="value">{ requesterDetails }</span>
            </div>
          </div>
          <TextArea
            id="ppwr-request-notes"
            light
            labelText={ t('ppwr.notes') }
            placeholder={ t('ppwr.notesPlaceholder') }
            maxLength={ 1000 }
            invalid={ !!requestErrors.notes?.message }
            invalidText={ requestErrors.notes?.message }
            disabled={ requestPending }
            { ...registerRequest('notes', validators.notes) }
          />
        </ModalBody>
        <ModalFooter>
          <Button
            type="button"
            kind="ghost"
            className="cancelRequest"
            disabled={ requestPending }
            onClick={ onCloseRequest }
          >
            { t('ppwr.cancel') }
          </Button>
          <Button
            type="button"
            kind="danger"
            disabled={ requestPending || !selectedProduct?.productId }
            onClick={ onSubmitRequest }
          >
            { requestPending && (
              <Loading active={ requestPending } small withOverlay={ false } />
            ) }
            { t(requestPending ? 'ppwr.sending' : 'ppwr.send') }
          </Button>
        </ModalFooter>
      </StyledRequestModal>
      { loading ? (
        <DataTableSkeleton showToolbar={ false } showHeader={ false } />
      ) : (
        <DataTable
          rows={ products.map(product => ({
            ...product,
            actions: product.productId,
          })) }
          headers={ headers }
        >
          {({ rows, headers: tableHeaders, getHeaderProps, getTableProps }) => (
            <TableContainer>
              <Table { ...getTableProps() }>
                <TableHead>
                  <TableRow>
                    { tableHeaders.map(header => (
                      <TableHeader
                        { ...getHeaderProps({ header }) }
                        key={ header.key }
                      >
                        { renderTableHeader(header) }
                      </TableHeader>
                    )) }
                  </TableRow>
                </TableHead>
                <TableBody>
                  { products.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={ tableHeaders.length }>
                        { t('ppwr.noResults') }
                      </TableCell>
                    </TableRow>
                  ) }
                  { rows.map(row => (
                    <TableRow key={ row.id }>
                      { row.cells.map(cell => (
                        <StyledTableCell
                          key={ cell.id }
                          data-label={ cell.info.header }
                          name={ tableHeaders.find(
                            header => header.key === cell.info.header
                          )?.header }
                        >
                          { renderCell(cell, row) }
                        </StyledTableCell>
                      )) }
                    </TableRow>
                  )) }
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DataTable>
      ) }
      <ServicePagination
        totalPages={ pagination?.totalPages }
        recordsPerPage={ pagination?.recordsPerPage }
        totalRecords={ pagination?.totalRecords }
        loading={ loading }
      />
    </StyledPPWR>
  );
};

PPWRComponent.propTypes = {
  products: array,
  pagination: shape({
    currentPage: number,
    recordsPerPage: number,
    totalPages: number,
    totalRecords: number,
  }),
  filters: object,
  loading: bool,
  notice: string,
  requester: object,
  selectedProduct: object,
  requestPending: bool,
  requestErrors: object,
  requestedIds: array,
  downloadingId: string,
  currentSortId: string,
  filterErrors: object,
  registerFilter: func,
  registerRequest: func,
  validators: object,
  onFilterChange: func,
  onApplyFilters: func,
  onClearFilters: func,
  onSort: func,
  onDismissNotice: func,
  onOpenRequest: func,
  onCloseRequest: func,
  onSubmitRequest: func,
  onDownload: func,
};

PPWRComponent.displayName = 'PPWRComponent';

export default PPWRComponent;
